/**
 * Agent View Page
 * Agent leaderboard with sorting, performance badges, trend deltas, and editable profiles
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowUpDown, ArrowUp, ArrowDown, Bot, UserCheck, ChevronRight, Pencil, Search } from 'lucide-react';
import MetricCard from '../components/dashboard/MetricCard';
import DateRangeFilter from '../components/dashboard/DateRangeFilter';
import AgentEditModal from '../components/dashboard/AgentEditModal';
import AgentSyncHint from '../components/dashboard/AgentSyncHint';
import Spinner from '../components/shared/Spinner';
import EmptyState from '../components/shared/EmptyState';
import RefreshButton from '../components/shared/RefreshButton';
import useDateRange from '../hooks/useDateRange';
import { getAgentLeaderboard } from '../services/analyticsService';
import { sentimentToPercent, qaToPercent } from '../utils/formatters';
import { onActivateKey } from '../utils/a11y';

const ScoreCell = ({ value, thresholds, suffix = '' }) => {
  if (value == null || value === '-') return <span className="text-gray-400">&mdash;</span>;
  const num = typeof value === 'number' ? value : parseFloat(value);
  const color = num >= (thresholds?.good ?? 80) ? 'text-green-600' : num >= (thresholds?.ok ?? 60) ? 'text-yellow-600' : 'text-red-600';
  return <span className={`font-semibold ${color}`}>{Math.round(num)}{suffix}</span>;
};

const SortableHeader = ({ label, sortKey, currentSort, currentDir, onSort }) => {
  const isActive = currentSort === sortKey;
  return (
    <th
      className="text-center px-5 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-300 focus:ring-inset"
      onClick={() => onSort(sortKey)}
      tabIndex={0}
      onKeyDown={onActivateKey(() => onSort(sortKey))}
      aria-sort={isActive ? (currentDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <div className="inline-flex items-center space-x-1">
        <span>{label}</span>
        {isActive ? (
          currentDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
        ) : (
          <ArrowUpDown size={12} className="text-gray-300" />
        )}
      </div>
    </th>
  );
};

const PerformanceBadge = ({ type }) => {
  if (type === 'top') {
    return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium ml-2">Top</span>;
  }
  if (type === 'attention') {
    return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium ml-2">Needs Attention</span>;
  }
  return null;
};

const AgentTypeBadge = ({ type }) => {
  if (type === 'ai') {
    return (
      <span className="inline-flex items-center space-x-0.5 text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-600">
        <Bot size={10} />
        <span>AI</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center space-x-0.5 text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">
      <UserCheck size={10} />
      <span>Human</span>
    </span>
  );
};

const getInitials = (name) => {
  const parts = (name || '?').trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : parts[0].substring(0, 2).toUpperCase();
};

const AgentViewPage = () => {
  const navigate = useNavigate();
  const { from, to, onChange } = useDateRange();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sortKey, setSortKey] = useState('total_interactions');
  const [sortDir, setSortDir] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [editAgent, setEditAgent] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getAgentLeaderboard({ from_date: from, to_date: to });
      if (result.success) {
        setAgents(result.data);
        setSummary(result.summary || null);
      } else {
        toast.error(result.error);
      }
    } catch (err) {
      toast.error('Failed to load agent data');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortedAgents = useMemo(() => {
    const sorted = [...agents].sort((a, b) => {
      const aVal = a[sortKey] ?? 0;
      const bVal = b[sortKey] ?? 0;
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [agents, sortKey, sortDir]);

  const filteredAgents = useMemo(() => {
    if (!searchTerm.trim()) return sortedAgents;
    const q = searchTerm.toLowerCase();
    return sortedAgents.filter(a =>
      (a.name || '').toLowerCase().includes(q) ||
      (a.agent_id || '').toLowerCase().includes(q) ||
      (a.phone_number || '').toLowerCase().includes(q)
    );
  }, [sortedAgents, searchTerm]);

  const performerSet = useMemo(() => {
    if (agents.length < 3) return {};
    const byScore = [...agents].sort((a, b) => (b.avg_sentiment || 0) - (a.avg_sentiment || 0));
    const topIds = new Set(byScore.slice(0, Math.max(1, Math.ceil(agents.length * 0.2))).map(a => a.id || a.agent_id));
    const bottomIds = new Set(byScore.slice(-Math.max(1, Math.ceil(agents.length * 0.2))).map(a => a.id || a.agent_id));
    const result = {};
    topIds.forEach(id => { result[id] = 'top'; });
    bottomIds.forEach(id => { if (!topIds.has(id)) result[id] = 'attention'; });
    return result;
  }, [agents]);

  if (loading) return <Spinner />;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <RefreshButton onClick={fetchData} loading={loading} />
        <DateRangeFilter from={from} to={to} onChange={onChange} />
      </div>

      {/* Summary cards with trend deltas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <MetricCard
          label="Total Agents"
          value={summary?.total_agents?.value ?? agents.length}
          change={summary?.total_agents?.change}
          changeLabel="vs prior period"
          color="#6366f1"
        />
        <MetricCard
          label="Avg Sentiment"
          value={(() => {
            const weighted = agents.reduce((acc, a) => {
              const w = a.total_interactions || 0;
              if (a.avg_sentiment == null || !w) return acc;
              return { sum: acc.sum + a.avg_sentiment * w, total: acc.total + w };
            }, { sum: 0, total: 0 });
            return weighted.total ? sentimentToPercent(weighted.sum / weighted.total) + '%' : '-';
          })()}
          change={summary?.avg_sentiment?.change}
          changeLabel="vs prior period"
          color="#22c55e"
        />
        <MetricCard
          label="Avg QA Score"
          value={(() => {
            const weighted = agents.reduce((acc, a) => {
              const w = a.total_interactions || 0;
              if (a.avg_qa_score == null || !w) return acc;
              return { sum: acc.sum + a.avg_qa_score * w, total: acc.total + w };
            }, { sum: 0, total: 0 });
            return weighted.total ? qaToPercent(weighted.sum / weighted.total) + '%' : '-';
          })()}
          change={summary?.avg_qa_score?.change}
          changeLabel="vs prior period"
          color="#3b82f6"
        />
        <MetricCard
          label="Total Interactions"
          value={summary?.total_interactions?.value ?? agents.reduce((s, a) => s + (a.total_interactions || 0), 0)}
          change={summary?.total_interactions?.change}
          changeLabel="vs prior period"
          color="#f59e0b"
        />
      </div>

      {/* Agent Leaderboard */}
      {agents.length === 0 ? (
        <EmptyState title="No agent data yet" description="Agent performance data appears when interactions are processed." />
      ) : (
        <div className="card">
          {/* Toolbar: heading, count, search */}
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-700">Agent Leaderboard</h3>
            <div className="flex items-center space-x-3">
              <span className="text-xs text-gray-400">
                Showing {filteredAgents.length} of {agents.length} agents
              </span>
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search agents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Agent</th>
                  <SortableHeader label="Interactions" sortKey="total_interactions" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Avg Handle Time" sortKey="avg_handle_time" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Sentiment" sortKey="avg_sentiment" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="QA Score" sortKey="avg_qa_score" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Resolution" sortKey="resolution_rate" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {filteredAgents.map((agent) => {
                  const agentKey = agent.id || agent.agent_id;
                  const displayName = agent.name || agent.agent_id;
                  // Phone number and raw ID, unless one of them already is the name
                  const secondary = [...new Set([agent.phone_number, agent.agent_id])]
                    .filter((value) => value && value !== displayName);
                  return (
                    <tr
                      key={agentKey || agent.name}
                      className="border-b border-gray-50 hover:bg-primary-50 cursor-pointer transition-all group focus:outline-none focus:ring-2 focus:ring-primary-300 focus:ring-inset"
                      onClick={() => navigate(`/agent/${agentKey}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`)}
                      tabIndex={0}
                      onKeyDown={onActivateKey(() => navigate(`/agent/${agentKey}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`))}
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center space-x-3">
                          {/* Avatar circle */}
                          <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {getInitials(displayName)}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-primary-700 truncate">
                                {displayName}
                              </span>
                              {agent.agent_type && <AgentTypeBadge type={agent.agent_type} />}
                              <AgentSyncHint status={agent.sync_status} />
                              <PerformanceBadge type={performerSet[agentKey]} />
                              {/* Edit icon (row hover) */}
                              {agent.profile_id && (
                                <button
                                  className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-gray-200 rounded"
                                  onClick={(e) => { e.stopPropagation(); setEditAgent(agent); }}
                                  aria-label={`Edit ${displayName}`}
                                >
                                  <Pencil size={12} className="text-gray-400" />
                                </button>
                              )}
                            </div>
                            {secondary.length > 0 && (
                              <p className="text-[11px] text-gray-400 truncate">{secondary.join(' · ')}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-center text-gray-700">{agent.total_interactions ?? '-'}</td>
                      <td className="px-5 py-3 text-center text-gray-700">
                        {agent.avg_handle_time != null ? `${Math.round(agent.avg_handle_time)}s` : '-'}
                      </td>
                      <td className="px-5 py-3 text-center">
                        <ScoreCell value={sentimentToPercent(agent.avg_sentiment)} thresholds={{ good: 70, ok: 50 }} suffix="%" />
                      </td>
                      <td className="px-5 py-3 text-center">
                        <ScoreCell value={qaToPercent(agent.avg_qa_score)} thresholds={{ good: 80, ok: 60 }} suffix="%" />
                      </td>
                      <td className="px-5 py-3 text-center">
                        <ScoreCell value={agent.resolution_rate || null} thresholds={{ good: 80, ok: 60 }} suffix="%" />
                      </td>
                      <td className="px-3 py-3 text-right">
                        <ChevronRight size={16} className="text-gray-300 group-hover:text-primary-600 transition-colors" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editAgent && (
        <AgentEditModal
          agent={editAgent}
          onClose={() => setEditAgent(null)}
          onSaved={fetchData}
        />
      )}
    </div>
  );
};

export default AgentViewPage;
